import React from "react";
import { handleAcousticCsvInputChange, handleMagneticCsvInputChange, handleSeismicCsvInputChange, handleZipInputChange, goToAdminPanel } from "../../tools/SystemManagement";
import { editSystem, getSystemInfo, sortObjectByKeys } from "../controller/EditSystemAction";
import { Grid, CardHeader, Tab, Tabs, Container, Box, Dialog, DialogTitle, DialogActions, DialogContent, DialogContentText, Switch, FormControlLabel} from '@material-ui/core';
import { withRouter } from 'react-router-dom';
import Button from "@material-ui/core/Button";
import applicationLogo from "../../../design/zeiss-logo-tagline_rgb.png"
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import FolderIcon from '@material-ui/icons/Folder';
import Avatar from '@material-ui/core/Avatar';
import WebBuilder from "../../tools/WebBuilder";
import { leaveConfirmVideoProcessing } from "../../../tools/constantMessages";
import { sortByPhysicalSize, sortByNumericValue } from '../../../utils/numberUtils';
import { VideoManager } from "../../video/VideoManager";

const TABS = {
    MAGNETIC: 0,
    ACOUSTIC: 1,
    SEISMIC: 2,
    MORE_INFO: 3
};

class AdminPanelEditSystemContainer extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            selectedCsvAcoustic: "",
            selectedCsvSeismic: "",
            selectedCsvMagnetic: "",
            selectedZip: "",
            selectedVideo: "",
            errorMessage: "",
            inputKey: Date.now(),
            loadingScreen: false,
            toggleLoadingScreenDisplay: false,
            systemName: localStorage.getItem("editingSystemName"),
            selectedTab: 0,
            isTabDisabled: [true, true, true],
            editorHtml: null,
            editorJson: null,
            moreInfoChanged: false,
            systemNotFound: false,
            enableAcousticPlotting: true,
            enableSeismicPlotting: true,
            plotSettingsChanged: false,
            magneticTable: [],
            acousticTable: [],
            seismicTable: [],
            imagesTable: [],
        };
        this.handleEditSystem = this.handleEditSystem.bind(this);
        this.handleGetSystemInfo = this.handleGetSystemInfo.bind(this);
    }
    
    componentDidMount() {
    this.handleGetSystemInfo(this.state.systemName);
}

    // Toggle handlers
    handleAcousticPlottingToggle = (event) => {
        this.setState({ 
            enableAcousticPlotting: event.target.checked,
            plotSettingsChanged: true 
        });
    }

    handleSeismicPlottingToggle = (event) => {
        this.setState({ 
            enableSeismicPlotting: event.target.checked,
            plotSettingsChanged: true 
        });
    }

    handleEditSystem = async (systemName, csvMagnetic, csvAcoustic, csvSeismic, zip) => {
    this.setState({ toggleLoadingScreenDisplay: true });
    
    let moreInfoToSave = null;
    let moreInfoProjectToSave = null;
    
    if (this.state.moreInfoChanged === true && this.editor) {
        moreInfoToSave = this.editor.runCommand('gjs-get-inlined-html');
        moreInfoProjectToSave = this.editor.getProjectData(); 
    }
    
    let response = await editSystem(
        systemName, 
        this.state.selectedCsvMagnetic,
        this.state.selectedCsvAcoustic,
        this.state.selectedCsvSeismic,
        this.state.selectedZip,
        moreInfoToSave,
        moreInfoProjectToSave,
        {
            acoustic: this.state.enableAcousticPlotting,
            seismic: this.state.enableSeismicPlotting
        }
    );
        
        this.setState({ toggleLoadingScreenDisplay: false });
        
        const isString = typeof response.data === "string" || response.data instanceof String;
        
        if (!isString) {
            if ('redirect' in response.data) {
                this.props.history.push('/login')
            }
            
            if (response.data && response.data.message) {
                this.setState({ plotSettingsChanged: false, moreInfoChanged: false });
                goToAdminPanel(this);
                return;
            }
            
            let problemsFound = "";
            for (const [key, value] of Object.entries(response.data)) {
                problemsFound += (key + ":\n")
                let index;
                for (index = 0; index < Object.values(value).length; index++) {
                    problemsFound += ("- " + Object.values(value)[index] + '\n');
                }
            }
            this.setState({
                errorMessage: problemsFound,
                inputKey: Date.now(),
                selectedCsvAcoustic: "",
                selectedCsvSeismic: "",
                selectedCsvMagnetic: "",
                selectedZip: "",
            })
        }
        else if (response.data === "The system was edited successfully.")
            goToAdminPanel(this);
        else this.setState({
            errorMessage: response['data'],
            selectedCsv: "",
            selectedZip: "",
            inputKey: Date.now()
        })
    }

    sortBeamEnergies = (beamEnergies) => {
    return beamEnergies.sort((a, b) => {
        const numA = parseInt(a);
        const numB = parseInt(b);
        return numA - numB;
    });
}

sortWorkingDistances = (distances) => {
    return distances.sort((a, b) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        return numA - numB;
    });
}

    handleGetSystemInfo = async (systemName) => {
        let response = await getSystemInfo(systemName);

        if ('redirect' in response) {
            this.props.history.push('/login')
        }
        if (response.status === 400) {
            this.setState({ systemNotFound: true });
            return;
        }
        
        if (response.data && response.data.plot_settings) {
            this.setState({
                enableAcousticPlotting: response.data.plot_settings.acoustic !== false,
                enableSeismicPlotting: response.data.plot_settings.seismic !== false,
            });
        } else {
            this.setState({
                enableAcousticPlotting: true,
                enableSeismicPlotting: true,
            });
        }
        // Sort WD values within each beam energy (unit-agnostic)
        const magneticData = response.data["magnetic"];
        if (magneticData && typeof magneticData === 'object') {
            for (let beamEnergy in magneticData) {
                if (magneticData[beamEnergy] && typeof magneticData[beamEnergy] === 'object') {
                    const wdKeys = Object.keys(magneticData[beamEnergy]);
                    if (wdKeys.length > 0) {
                        const sortedKeys = sortByPhysicalSize(wdKeys);
                        const sortedWDs = {};
                        sortedKeys.forEach(wd => {
                            sortedWDs[wd] = magneticData[beamEnergy][wd];
                        });
                        magneticData[beamEnergy] = sortedWDs;
                    }
                }
            }
            response.data["magnetic"] = magneticData;
        }
        
        // Also sort acoustic data if needed (frequencies)
        const acousticData = response.data["acoustic"];
        if (acousticData && typeof acousticData === 'object') {
            const acousticKeys = Object.keys(acousticData).filter(key => key !== 'enable' && key !== 'available');
            if (acousticKeys.length > 0) {
                const sortedAcousticKeys = sortByNumericValue(acousticKeys);
                const sortedAcoustic = {};
                sortedAcousticKeys.forEach(key => {
                    sortedAcoustic[key] = acousticData[key];
                });
                if (acousticData.enable !== undefined) sortedAcoustic.enable = acousticData.enable;
                if (acousticData.available !== undefined) sortedAcoustic.available = acousticData.available;
                response.data["acoustic"] = sortedAcoustic;
            }
        }
        
        // Also sort seismic data if needed
        const seismicData = response.data["seismic"];
        if (seismicData && typeof seismicData === 'object') {
            const seismicKeys = Object.keys(seismicData).filter(key => key !== 'enable' && key !== 'available');
            if (seismicKeys.length > 0) {
                const sortedKeys = sortByNumericValue(seismicKeys);
                const sortedSeismic = {};
                sortedKeys.forEach(key => {
                    sortedSeismic[key] = seismicData[key];
                });
                if (seismicData.enable !== undefined) sortedSeismic.enable = seismicData.enable;
                if (seismicData.available !== undefined) sortedSeismic.available = seismicData.available;
                response.data["seismic"] = sortedSeismic;
            }
        }
        
        var magneticTable = [];
        var acousticTable = [];
        var seismicTable = [];
        var imagesTable = [];
        if (response.data["magnetic"]["info"]) {
    response.data["magnetic"] = sortObjectByKeys(Object.keys(response.data["magnetic"]), response.data["magnetic"]);
}
        const orderedKeysAcoustic = Object.keys(response.data["acoustic"]).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))

        response.data.generated_images = sortObjectByKeys(
            Object.keys(response.data.generated_images),
            response.data.generated_images)

        let index;
        if (response.data["seismic"]["available"] === true && Object.keys(response.data["seismic"]).length > 2) {
            var seismicTableHeader = [<td key={"titleSystemName"}><b>System name</b></td>, <td key={"titleFreq"}><b>Frequency (Hz)</b></td>];
            index = 0
            for (const entry in Object.entries(response.data["seismic"])[0][1]) {
                seismicTableHeader.push(<td key={entry + index}><b>{entry}</b></td>);
                index++;
            }
            seismicTable.push(
                <thead key={"tableHead"}>
                    <tr key={"trHeadSeismic"}>
                        {seismicTableHeader}
                    </tr>
                </thead>
            )
            index = 0;
            for (const [key, value] of Object.entries(response.data["seismic"])) {
                if (key === "enable" || key === "available")
                    continue;
                var innerSeismicTable = [<td key={"systemName" + index}>{systemName}</td>, <td key={"key" + index}>{key}</td>]
                let innerIndex = 0;
                for (const innerValue of Object.values(value)) {
                    innerSeismicTable.push(<td key={"innerVal" + innerIndex}>{innerValue}</td>)
                    innerIndex++;
                }
                seismicTable.push(
                    <tbody key={"innerWrapper" + index}>
                        <tr key={index}>
                            {innerSeismicTable}
                        </tr>
                    </tbody>
                )
                index++
            }
        }
        
        imagesTable.push(<thead key={"tHeadImages"}>
            <tr key={"tHeadTrImages"}>
                <th key={"beamEnergy"}>
                    Beam energy
                </th>
                <th key={"sourceImage"}>
                    Source image
                </th>
                <th key={"magnification"}>
                    Magnification of the generated images
                </th>
            </tr>
        </thead>)
        
        if (response.data["acoustic"]["available"] === true && orderedKeysAcoustic.length > 2) {
            index = 0
            var acousticTableHeader = [<td key={"sysNameTitle"}><b>System name</b></td>, <td key={"frequencyTitle"}><b>Frequency (Hz)</b></td>];
            for (const entry in Object.entries(response.data["acoustic"])[0][1]) {
                acousticTableHeader.push(<td key={index + "td"}><b key={index + "b"}>{entry}</b></td>);
                index++;
            }
            acousticTable.push(
                <thead key={systemName + "HeadAcoustic" + index}>
                    <tr key={systemName + "trAcoustic" + index}>
                        {acousticTableHeader}
                    </tr>
                </thead>
            )
            index = 1;
            for (let key of orderedKeysAcoustic) {
                if (key === "enable" || key === "available")
                    continue;
                const value = response.data["acoustic"][key];
                acousticTable.push(
                    <tbody key={index}>
                        <tr key={index}>
                            <td key={"sysName" + index}>
                                {systemName}
                            </td>
                            <td key={"key" + index}>
                                {key}
                            </td>
                            <td key={"sens" + index}>
                                {value["Sensitivity"]}
                            </td>
                            <td key={"threshold" + index}>
                                {value["Threshold"]}
                            </td>
                        </tr>
                    </tbody>
                )
                index++;
            }
        }
        
        if (response.data["magnetic"]["available"] === true) {
            let magneticData = null;
            
            if (response.data["magnetic"]["info"]) {
                magneticData = response.data["magnetic"]["info"];
            } else {
                magneticData = {};
                for (let key in response.data["magnetic"]) {
                    if (key !== "enable" && key !== "available" && key !== "info") {
                        magneticData[key] = response.data["magnetic"][key];
                    }
                }
            }
            
            if (magneticData && Object.keys(magneticData).length > 0) {
                index = 0;
                magneticTable.push(
                    <thead key={"magneticHead"}>
                        <tr key={"magneticTrHead"}>
                            <th key={"titleSysName"}>System name</th>
                            <th key={"titleBeamInt"}>Beam energy</th>
                            <th key={"titleBWD"}>Working distance</th>
                            <th key={"titlemG_x"}>nm/mG_x</th>
                            <th key={"title_y"}>nm/mG_y</th>
                            <th key={"title_z"}>nm/mG_z</th>
                        </tr>
                    </thead>
                );
                
const sortedBeams = this.sortBeamEnergies(Object.keys(magneticData));

for (const beamEnergy of sortedBeams) {
    const beamData = magneticData[beamEnergy];
    
    const sortedWDs = this.sortWorkingDistances(Object.keys(beamData));
    
    for (const wd of sortedWDs) {
        const values = beamData[wd];
                        magneticTable.push(
                            <tbody key={"body" + index}>
                                <tr key={index}>
                                    <td key={"systemName" + index}>{systemName}</td>
                                    <td key={"key" + index}>{beamEnergy}</td>
                                    <td key={"innerKey" + index}>{wd}</td>
                                    <td key={"innerValueX" + index}>{values["nm/mG_x"] || "N/A"}</td>
                                    <td key={"innerValueY" + index}>{values["nm/mG_y"] || "N/A"}</td>
                                    <td key={"innerValueZ" + index}>{values["nm/mG_z"] || "N/A"}</td>
                                </tr>
                            </tbody>
                        );
                        index++;
                    }
                }
            }
        }

        response.data.seismic = sortObjectByKeys(
            Object.keys(response.data.seismic),
            response.data.seismic)

        let tableValues = [];
        for (const [key, value] of Object.entries(response.data.generated_images)) {
            let index = 0;
            let bigIndex = 0;
            let magnifications = [];
            for (bigIndex = 0; bigIndex < Object.values(value).length; bigIndex++) {
                let currentMagnifications = "";
                for (index = 0; index < Object.values(value)[bigIndex].length; index++) {
                    currentMagnifications += Object.values(value)[bigIndex][index] + ", ";
                }
                currentMagnifications = currentMagnifications.substring(0, currentMagnifications.length - 2);
                magnifications.push(currentMagnifications);
            }
            for (bigIndex = 0; bigIndex < Object.values(value).length; bigIndex++) {
                if (bigIndex === 0) {
                    tableValues.push(
                        <tr key={bigIndex + key}>
                            <td key={bigIndex + ", 0"} rowSpan={Object.values(value).length.toString()}>
                                {key}
                            </td>
                            <td key={bigIndex + ", 1"}>
                                {Object.keys(value)[0]}
                            </td>
                            <td key={bigIndex + ", 2"}>
                                {magnifications[0]}
                            </td>
                        </tr>
                    )
                    continue;
                }
                tableValues.push(
                    <tr key={bigIndex + key}>
                        <td key={bigIndex + ", 0"}>
                            {Object.keys(value)[bigIndex]}
                        </td>
                        <td key={bigIndex + ", 1"}>
                            {magnifications[bigIndex]}
                        </td>
                    </tr>
                )
            }
        }
        
        imagesTable.push(
            <tbody key={"tableBody"}>
                {tableValues}
            </tbody>
        )

        this.setState({
            magneticTable: magneticTable,
            acousticTable: acousticTable,
            imagesTable: imagesTable,
            seismicTable: seismicTable,
            isTabDisabled: [!response.data["magnetic"]["enable"], !response.data["acoustic"]["enable"], !response.data["seismic"]["enable"]],
        })
        
    // Store the project data in state for later
    if (response.data["more_info"] && response.data["more_info"]["available"] === true) {
        try {
            const projectData = response.data["more_info"]["project"];
            this.setState({ 
                editorJson: typeof projectData === 'string' ? projectData : JSON.stringify(projectData),
                editorHtml: response.data["more_info"]["info"]
            });
        } catch (error) {
            console.error("Error parsing project data:", error);
        }
    }
}

    handleTabChange = (event, value) => {
    this.setState({ selectedTab: value });
    
    // If switching to More Info tab and we have project data
    if (value === TABS.MORE_INFO && this.state.editorJson && this.editor) {
        setTimeout(() => {
            try {
                const projectData = typeof this.state.editorJson === 'string' 
                    ? JSON.parse(this.state.editorJson) 
                    : this.state.editorJson;
                this.editor.loadProjectData(projectData);
            } catch (error) {
                console.error("Error loading project on tab change:", error);
            }
        }, 200);
    }
};

    render() {
        return <>
            <Container>
                {this.state.toggleLoadingScreenDisplay === true ? 
                    <div style={{
                        top: "50%",
                        left: "50%",
                        marginTop: "-50px",
                        marginLeft: "-100px",
                        position: "absolute",
                        width: "200px",
                        height: "200px"
                    }}>
                        <img alt="App logo" src={applicationLogo} style={{
                            width: "80%",
                            height: "80%",
                            position: "absolute",
                            left: "10%",
                            top: "5%"
                        }} />
                        <CircularProgress style={{
                            position: "absolute",
                            width: "100%",
                            height: "100%"
                        }} />
                    </div> :
                    <Grid container direction="column">
                        <Dialog
                            open={this.state.systemNotFound === true}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">
                                {"Error!"}
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    {"The system does not exist!"}
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => goToAdminPanel(this)}>Ok</Button>
                            </DialogActions>
                        </Dialog>
                        <Grid container className="page_header">
                            <Grid className="page">
                                <Grid container direction="row" justifyContent="center" alignItems="center">
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <CardHeader
                                            avatar={
                                                <Avatar variant={"square"} aria-label="ripple-simulator" src={applicationLogo}
                                                    style={{
                                                        width: "80px",
                                                        height: "80px",
                                                    }} />
                                            }
                                            titleTypographyProps={{ variant: "h4", color: 'textPrimary' }}
                                            title={"Ripple Simulator - System Editor"}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container direction="column">
                            <Grid container spacing={6}>
                                <Grid item>
                                    <Typography style={{ fontSize: 30 }}>
                                        {this.state.systemName}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography key={"currentSettings"} style={{ fontSize: 30 }}>
                                        CURRENT SETTINGS
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Typography>
                                <FolderIcon />
                                {" "}System folder name: /{this.state.systemName}
                            </Typography>
                            <Grid container spacing={2} style={{ marginTop: "25px" }}>
                                <Grid item>
                                    <Button variant="outlined" onClick={() => this.handleEditSystem(this.state.systemName, this.state.selectedCsvMagnetic, this.state.selectedCsvAcoustic, this.state.selectedCsvSeismic, this.state.selectedZip)}
                                        disabled={this.state.selectedCsvAcoustic === "" && this.state.selectedCsvMagnetic === "" && this.state.selectedCsvSeismic === "" && this.state.selectedZip === "" && this.state.moreInfoChanged === false  && this.state.plotSettingsChanged === false}>
                                        Update System
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button variant="outlined" onClick={() => goToAdminPanel(this)}>
                                        Back to admin panel
                                    </Button>
                                </Grid>
                            </Grid>
                            <Typography style={{ color: "red", marginTop: "20px", whiteSpace: 'pre-line' }}>
                                {this.state.errorMessage}
                            </Typography>
                            <Grid container spacing={10}>
                                <Grid item xs={12} md={12}>
                                    <Tabs indicatorColor="primary" value={this.state.selectedTab} onChange={this.handleTabChange} style={{ width: "max-content" }}>
                                        <Tab label={(<Typography style={{ fontSize: "13px" }}>Magnetic distortion</Typography>)} />
                                        <Tab label={(<Typography style={{ fontSize: "13px" }}>Acoustic distortion</Typography>)} />
                                        <Tab label={(<Typography style={{ fontSize: "13px" }}>Seismic distortion</Typography>)} />
                                        <Tab label={(<Typography style={{ fontSize: "13px" }}>More information</Typography>)} />
                                    </Tabs>
                                    
                                    {/* Magnetic Tab */}
                                    <Grid container style={{ display: this.state.selectedTab === TABS.MAGNETIC ? "block" : "none" }}>
                                        <Typography style={{ marginTop: 20 }}>
                                            <b>CSV table for magnetic sensitivity</b>
                                        </Typography>
                                        <form>
                                            <input type="file" name="file" key={this.state.inputKey} onChange={event => this.setState(handleMagneticCsvInputChange(event, this))} accept=".csv" />
                                        </form>
                                        
                                        <div>
                                            {this.state.magneticTable !== undefined && this.state.magneticTable.length > 0 &&
                                                <Typography style={{ fontWeight: "bold", marginBottom: "10px", marginTop: "20px" }}>
                                                    Distortion values
                                                </Typography>}
                                            <table>
                                                {this.state.magneticTable}
                                            </table>
                                        </div>
                                    </Grid>
                                    
                                    {/* Acoustic Tab */}
                                    <Grid style={{ display: this.state.selectedTab === TABS.ACOUSTIC ? "block" : "none", paddingBottom: "0px" }}>
                                        <Typography style={{ marginTop: 20 }}>
                                            <b>CSV table for acoustic sensitivity</b>
                                        </Typography>
                                        <form>
                                            <input type="file" name="file" key={this.state.inputKey} onChange={event => this.setState(handleAcousticCsvInputChange(event, this))} accept=".csv" />
                                        </form>
                                        
                                        {/* Plotting Toggle */}
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    color="primary" 
                                                    checked={this.state.enableAcousticPlotting} 
                                                    onChange={this.handleAcousticPlottingToggle} 
                                                />
                                            }
                                            label={<span style={{ fontWeight: 'bold' }}>SEM Resonance Plotting</span>}
                                            labelPlacement="end"
                                            style={{ marginTop: 20, marginBottom: 10 }}
                                        />
                                        
                                        {this.state.acousticTable !== undefined && this.state.acousticTable.length > 0 &&
                                            <Typography style={{ fontWeight: "bold", marginBottom: "10px", marginTop: "20px" }}>
                                                Distortion values
                                            </Typography>}
                                        <table>
                                            {this.state.acousticTable}
                                        </table>
                                    </Grid>
                                    
                                    {/* Seismic Tab */}
                                    <Grid style={{ display: this.state.selectedTab === TABS.SEISMIC ? "block" : "none", paddingBottom: "0px" }}>
                                        <Typography style={{ fontWeight: "bold", marginTop: "20px" }}>
                                            CSV table for seismic sensitivity
                                        </Typography>
                                        <form>
                                            <input type="file" name="file" key={this.state.inputKey} onChange={event => this.setState(handleSeismicCsvInputChange(event, this))} accept=".csv" />
                                        </form>
                                        
                                        {/* Plotting Toggle */}
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    color="primary" 
                                                    checked={this.state.enableSeismicPlotting} 
                                                    onChange={this.handleSeismicPlottingToggle} 
                                                />
                                            }
                                            label={<span style={{ fontWeight: 'bold' }}>SEM Resonance Plotting</span>}
                                            labelPlacement="end"
                                            style={{ marginTop: 20, marginBottom: 10 }}
                                        />
                                        
                                        {this.state.seismicTable !== undefined && this.state.seismicTable.length > 0 &&
                                            <Typography style={{ fontWeight: "bold", marginBottom: "10px", marginTop: "20px" }}>
                                                Distortion values
                                            </Typography>}
                                        <table>
                                            {this.state.seismicTable}
                                        </table>
                                    </Grid>
                                    
                                    {/* More Info Tab */}
{this.state.selectedTab === TABS.MORE_INFO && (
    <div style={{ width: "100%", maxWidth: "100%" }}>
        <VideoManager history={this.props.history} inputKey={this.state.inputKey} />
        <div style={{ width: "93%", marginTop: "20px" }}>
            <WebBuilder
                key={this.state.inputKey}
                initialData={this.state.editorJson ? (typeof this.state.editorJson === 'string' ? JSON.parse(this.state.editorJson) : this.state.editorJson) : null}
                onInit={(editorInstance) => {
                    this.editor = editorInstance;
                    
                    editorInstance.on("update", () => {
                        this.setState({
                            moreInfoChanged: true,
                            editorJson: JSON.stringify(editorInstance.getProjectData())
                        });
                    });
                    
                    if (this.state.editorJson && editorInstance.getWrapper()) {
                        setTimeout(() => {
                            try {
                                const projectData = typeof this.state.editorJson === 'string' 
                                    ? JSON.parse(this.state.editorJson) 
                                    : this.state.editorJson;
                                editorInstance.loadProjectData(projectData);
                            } catch (error) {
                                console.error("Error loading project:", error);
                            }
                        }, 200);
                    }
                }}
            />
        </div>
    </div>
)}
                                </Grid>
                                
                                <Grid item xs={12} md={5}>
                                    {this.state.selectedTab !== TABS.MORE_INFO && (
                                        <Grid>
                                            <Box sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' } }}>
                                                <Typography style={{ marginTop: 50 }}>
                                                    <b>Images</b>
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: { xs: 'block', sm: 'block', md: 'block', lg: 'none' } }}>
                                                <Typography>
                                                    <b>Images</b>
                                                </Typography>
                                            </Box>
                                            <form>
                                                <input type="file" key={this.state.inputKey} onChange={event => this.setState(handleZipInputChange(event, this))} accept=".zip" />
                                            </form>
                                            <Typography key={"magnifications"} style={{ fontWeight: "bold", marginBottom: "10px", marginTop: "20px" }}>
                                                Available magnifications
                                            </Typography>
                                            <table key={"imagesTable"}>
                                                {this.state.imagesTable}
                                            </table>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Container>
        </>;
    }
}

export default withRouter(AdminPanelEditSystemContainer);